import React, { useContext, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auroraTopbarSx } from '../theme/auroraSurface';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Container,
  Typography,
  Badge,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Home,
  LocalLibrary,
  Notifications,
  Psychology,
  AccountCircle,
  ExitToApp,
  QrCode2,
  Search
} from '@mui/icons-material';
import { AuthContext } from '../context/AuthContext';

function DesktopNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, logout } = useContext(AuthContext);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleMenuClose();
    navigate('/');
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Menu items based on user role
  const menuItems = isAuthenticated
    ? user?.role === 'librarian'
      ? [
          // Librarian menu items (no Dashboard)
          { label: 'Home', icon: <Home />, path: '/librarian' },
        ]
      : [
          // Student menu items
          { label: 'Home', icon: <Home />, path: '/student' },
          { label: 'Libraries', icon: <LocalLibrary />, path: '/libraries' },
        ]
    : [
        // Guest menu items
        { label: 'Home', icon: <Home />, path: '/' },
        { label: 'Libraries', icon: <LocalLibrary />, path: '/libraries' },
      ];

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={(theme) => ({
        ...auroraTopbarSx(theme),
        borderBottom: 1,
        borderColor: 'divider',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        top: 0,
        zIndex: 1100,
      })}
    >
      <Container maxWidth="xl">
        <Toolbar sx={{ py: { xs: 0.5, md: 1 }, minHeight: { xs: 56, md: 64 } }}>
          {/* Logo/Brand */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1, md: 1.5 },
              cursor: 'pointer',
              mr: { md: 2 },
              flexShrink: 0,
            }}
            onClick={() => navigate('/')}
          >
            <Box
              sx={{
                width: { xs: 36, md: 40 },
                height: { xs: 36, md: 40 },
                borderRadius: 2,
                background: 'linear-gradient(135deg, #F26522 0%, #F7B32B 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 800,
                fontSize: { xs: '1rem', md: '1.2rem' },
              }}
            >
              BML
            </Box>
            <Typography
              variant="h6"
              fontWeight="800"
              sx={(theme) => ({
                color: theme.palette.mode === 'light' ? theme.palette.primary.dark : theme.palette.primary.light,
                fontSize: { xs: '1rem', md: '1.25rem' },
                display: { xs: 'none', sm: 'block' },
              })}
            >
              BookMyLibrary
            </Typography>
          </Box>

          {/* Search Bar - Full width for students and guests */}
          {(!isAuthenticated || user?.role === 'student') ? (
            <Box
              onClick={() => navigate('/libraries')}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: { xs: 0.75, md: 1 },
                bgcolor: 'action.hover',
                borderRadius: 3,
                cursor: 'pointer',
                flexGrow: 1,
                mx: { xs: 1, md: 2 },
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': {
                  bgcolor: 'action.selected',
                  borderColor: 'primary.main',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <Search sx={{ fontSize: 20, color: 'text.secondary' }} />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: '0.875rem' }}
              >
                Search
              </Typography>
            </Box>
          ) : (
            <>
              {/* Navigation Menu Items - Desktop Only */}
              <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1, flexShrink: 0 }}>
                {menuItems.map((item) => (
                  <Button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    startIcon={item.icon}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      color: isActive(item.path) ? '#1B365D' : 'text.primary',
                      bgcolor: isActive(item.path) ? 'rgba(242, 101, 34, 0.1)' : 'transparent',
                      '&:hover': {
                        bgcolor: isActive(item.path) ? 'rgba(242, 101, 34, 0.15)' : 'action.hover',
                      },
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
              </Box>
              {/* Search Bar - Guest */}
              <Box
                onClick={() => navigate('/libraries')}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: { xs: 0.75, md: 1 },
                  bgcolor: 'action.hover',
                  borderRadius: 3,
                  cursor: 'pointer',
                  flexGrow: 1,
                  mx: { xs: 1, md: 2 },
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': {
                    bgcolor: 'action.selected',
                    borderColor: 'primary.main',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <Search sx={{ fontSize: 20, color: 'text.secondary' }} />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: '0.875rem' }}
                >
                  Search
                </Typography>
              </Box>
            </>
          )}

          {/* Right Side Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 1.5 } }}>
            {isAuthenticated ? (
              <>
                {/* Notifications - Desktop Only */}
                <IconButton sx={{ display: { xs: 'none', md: 'inline-flex' } }}>
                  <Badge badgeContent={0} color="error">
                    <Notifications />
                  </Badge>
                </IconButton>

                {/* Focus Mode - Desktop Only, Students Only */}
                {user?.role === 'student' && (
                  <IconButton
                    onClick={() => navigate('/student/focus-mode')}
                    sx={{
                      display: { xs: 'none', md: 'inline-flex' },
                      bgcolor: 'rgba(74, 144, 164, 0.1)',
                      color: '#4A90A4',
                      '&:hover': { bgcolor: 'rgba(74, 144, 164, 0.2)' },
                    }}
                  >
                    <Psychology />
                  </IconButton>
                )}

                {/* User Menu - Desktop Only */}
                <IconButton onClick={handleMenuOpen} sx={{ display: { xs: 'none', md: 'inline-flex' } }}>
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      background: 'linear-gradient(135deg, #F26522 0%, #F7B32B 100%)',
                      fontWeight: 700,
                      fontSize: '0.9rem',
                    }}
                  >
                    {user?.name?.charAt(0)}
                  </Avatar>
                </IconButton>

                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  PaperProps={{
                    sx: { mt: 1, minWidth: 200, borderRadius: 2 },
                  }}
                >
                  <Box sx={{ px: 2, py: 1.5 }}>
                    <Typography variant="subtitle2" fontWeight="700">
                      {user?.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {user?.email}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, textTransform: 'capitalize' }}>
                      {user?.role}
                    </Typography>
                  </Box>
                  <Divider />

                  {/* Student-specific menu items */}
                  {user?.role === 'student' && (
                    <>
                      <MenuItem
                        onClick={() => {
                          navigate('/student/profile');
                          handleMenuClose();
                        }}
                      >
                        <AccountCircle sx={{ mr: 1.5, fontSize: 20 }} />
                        Profile
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          navigate('/student/qr-pass');
                          handleMenuClose();
                        }}
                      >
                        <QrCode2 sx={{ mr: 1.5, fontSize: 20 }} />
                        Digital Pass
                      </MenuItem>
                      <Divider />
                    </>
                  )}

                  <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                    <ExitToApp sx={{ mr: 1.5, fontSize: 20 }} />
                    Sign Out
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/login')}
                  sx={{
                    display: { xs: 'none', md: 'inline-flex' },
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 2,
                    px: 3,
                  }}
                >
                  Sign In
                </Button>
                <Button
                  variant="contained"
                  onClick={() => navigate('/register')}
                  sx={{
                    display: { xs: 'none', sm: 'inline-flex' },
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 2,
                    px: 3,
                    background: 'linear-gradient(135deg, #F26522 0%, #C94E10 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #FF8A50 0%, #F26522 100%)',
                    },
                  }}
                >
                  Sign Up
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => navigate('/login')}
                  sx={{
                    display: { xs: 'inline-flex', sm: 'none' },
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #F26522 0%, #C94E10 100%)',
                    px: 2,
                    py: 0.5,
                  }}
                >
                  Login
                </Button>
              </>
            )}
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}

export default DesktopNav;
